import { RootState as CentralRootState, useTypedSelector } from 'lib/centralStore';
import { IppicaScommesseEsitoTipoBoxMinMaxProps, IppicaScommesseEsitoTipoBoxProps } from '.';
import { convertDecimalQuota, isQuotaDisabled, isStatoCavalloRetired } from 'features/ippica/functions';
import { getQuotaValue, isQuotaPlayable } from 'features/ippica/utils/utils';
import {
  selectCodiceStatoCavallo,
  selectCodiceTipoScommessaPiazzatoMinMax,
  selectCurrentScommesseType,
  selectQuotaIppica,
  selectQuoteAntepost,
  selectSiglaSistemaPiazzatoMinMax,
  selectSiglaSistemaVincente,
  selectTipoScommessaFromEsitoKey,
  selectTipoScommessaFromEsitoKeyTot,
  selectTipoScommessaList,
} from 'features/ippica/selectors';
import { useCartToggleHandler, useIsInCart } from 'features/carrello/hooks';

import { IppicaScommesseType } from 'features/ippica/types';
import { KeyManagerIppica } from 'features/ippica/utils/keyManagerIppica';
import { breakpoint } from 'utility/constant';
import classNames from 'classnames';
import styles from './IppicaScommesseEsitoTipoBox.module.scss';
import { useMemo } from 'react';
import useWindowSize, { enComparer } from 'hooks/useWindowSize';

export const IppicaScommesseEsitoTipoBox = ({
  label,
  isSmall,
  keyQuota,
  keyCavallo,
  containerClassName,
}: IppicaScommesseEsitoTipoBoxProps) => {
  const currentTypeScommessa = useTypedSelector(selectCurrentScommesseType);
  const isNotQFType = currentTypeScommessa !== IppicaScommesseType.QF;

  const key = new KeyManagerIppica(keyQuota);
  const selectedQuota = useTypedSelector(selectQuotaIppica(keyCavallo, keyQuota));

  const siglaSistemaVincente = useTypedSelector(selectSiglaSistemaVincente);

  const codiceStatoCavallo = useTypedSelector(selectCodiceStatoCavallo(keyCavallo));
  const { value: quotaValue, infoAggId: infoAgg } = selectedQuota ?? {};

  //NOTE: FORMATO ERRATO DI KEY CAVALLO, CORRETTA L'ASSEGNAZIONE A NUMERO CAVALLO
  key.numeroCavallo = new KeyManagerIppica(keyCavallo).codiceTipoScommessa!;
  if (isNotQFType && siglaSistemaVincente) {
    key.codiceTipoScommessa = siglaSistemaVincente.key ?? '';
  }
  if (infoAgg) {
    key.infoAggiuntiva = infoAgg;
  }

  const tipoScommessa = useTypedSelector((state: CentralRootState) =>
    isNotQFType
      ? selectTipoScommessaFromEsitoKeyTot(state, key.esitoKey)
      : selectTipoScommessaFromEsitoKey(state, key.esitoKey)
  );

  const isDisabled = useMemo(() => {
    if (isStatoCavalloRetired(codiceStatoCavallo)) return true;
    const props = { quota: quotaValue, statoScommessa: tipoScommessa?.codiceStatoTipoScommessa };
    return isQuotaDisabled(props);
  }, [codiceStatoCavallo, quotaValue, tipoScommessa?.codiceStatoTipoScommessa]);

  const quota = getQuotaValue(isDisabled, selectedQuota);
  const cartHandler = useCartToggleHandler(key.esitoKey);
  const isActiveInCart = useIsInCart(key.esitoKey);

  return (
    <ScommesseTipoBoxUiContainer
      label={label}
      quota={quota}
      isSmall={isSmall}
      isDisable={isDisabled}
      scommessaKey={key.esitoKey}
      isActiveInCart={isActiveInCart}
      containerClassName={containerClassName}
      cartHandler={cartHandler}
    />
  );
};

export const IppicaScommesseEsitoTipoBoxMinMax = ({
  label,
  isSmall,
  keyCavallo,
  containerClassName,
  quotaCombinataStatica,
}: IppicaScommesseEsitoTipoBoxMinMaxProps) => {
  const codiceTipoScommessaPiazzatoMinMax = useTypedSelector(selectCodiceTipoScommessaPiazzatoMinMax);
  const siglaSistemaPiazzatoMinMax = useTypedSelector(
    selectSiglaSistemaPiazzatoMinMax(codiceTipoScommessaPiazzatoMinMax!)
  );
  const codiceStatoCavallo = useTypedSelector(selectCodiceStatoCavallo(keyCavallo));
  const { codiceProgramma, numeroAvvenimento, codiceTipoScommessa } = new KeyManagerIppica(keyCavallo);
  const key = new KeyManagerIppica({
    codiceProgramma: String(codiceProgramma),
    numeroAvvenimento: String(numeroAvvenimento),
    codiceTipoScommessa: siglaSistemaPiazzatoMinMax?.key ?? '',
    infoAggiuntiva: '000000',
    codiceCavallo: String(codiceTipoScommessa),
  });
  const cartHandler = useCartToggleHandler(key.esitoKey!);
  const isActiveInCart = useIsInCart(key.esitoKey!);
  const isDisabled = isStatoCavalloRetired(codiceStatoCavallo);
  return (
    <ScommesseTipoBoxUiContainer
      label={label}
      quota={quotaCombinataStatica}
      isSmall={isSmall}
      isDisable={isDisabled}
      scommessaKey={key.esitoKey}
      isActiveInCart={isActiveInCart}
      containerClassName={containerClassName}
      cartHandler={cartHandler}
    />
  );
};

export type VirtualScommesseEsitoTipoBoxProps = {
  label?: string;
  quota?: number | string;
  isSmall?: boolean;
  isDisable?: boolean;
  scommessaKey?: string; // VIRTUAL
  containerClassName?: string;
};

export const VirtualScommesseEsitoTipoBox = ({
  label,
  quota,
  isSmall,
  isDisable,
  scommessaKey,
  containerClassName,
}: VirtualScommesseEsitoTipoBoxProps) => {
  const cartHandler = useCartToggleHandler(scommessaKey!);
  const isActiveInCart = useIsInCart(scommessaKey!);
  return (
    <ScommesseTipoBoxUiContainer
      label={label}
      quota={quota}
      isSmall={isSmall}
      isDisable={isDisable}
      scommessaKey={scommessaKey}
      isQuotaVirtual
      isActiveInCart={isActiveInCart}
      containerClassName={containerClassName}
      cartHandler={cartHandler}
    />
  );
};

export type IppicaScommesseEsitoAntepostTipoBoxProps = {
  label?: string;
  // quota?: number | string;
  isSmall?: boolean;
  keyQuota: string;
  isDisable?: boolean;
  keyCavallo: string;
  scommessaKey?: string; // VIRTUAL
  isNotClickable?: string;
  containerClassName?: string;
};

export const IppicaScommesseEsitoAntepostTipoBox = ({
  label,
  isSmall,
  keyQuota,
  isDisable,
  keyCavallo,
  containerClassName,
}: IppicaScommesseEsitoAntepostTipoBoxProps) => {
  const quota = useTypedSelector(selectQuoteAntepost(keyCavallo, keyQuota));
  const tipoScommessa = useTypedSelector(selectTipoScommessaList);
  const key = new KeyManagerIppica(keyQuota);
  const selectedQuota = useTypedSelector(selectQuotaIppica(keyCavallo, keyQuota));

  key.numeroCavallo = new KeyManagerIppica(keyCavallo).codiceTipoScommessa!;
  key.codiceTipoScommessa = tipoScommessa?.[0]?.codiceTipoScommessa?.toString() ?? '';

  const { infoAggId } = selectedQuota ?? {};

  if (infoAggId && isQuotaPlayable(isDisable ?? false, selectedQuota)) {
    key.infoAggiuntiva = infoAggId;
  }

  const cartHandler = useCartToggleHandler(key.esitoKey);
  const isActiveInCart = useIsInCart(key.esitoKey);

  const isDisabled = useMemo(() => {
    const props = { quota, statoScommessa: tipoScommessa?.[0]?.codiceStatoTipoScommessa };
    return isQuotaDisabled(props);
  }, [quota, tipoScommessa]);

  return (
    <ScommesseTipoBoxUiContainer
      label={label}
      isSmall={isSmall}
      isDisable={isDisabled}
      quota={!isDisabled ? quota : ''}
      containerClassName={containerClassName}
      scommessaKey={key.esitoKey}
      cartHandler={cartHandler}
      isActiveInCart={isActiveInCart}
    />
  );
};

export type ScommesseTipoBoxUiContainerProps = {
  label?: string;
  quota?: number | string;
  isSmall?: boolean;
  isDisable?: boolean;
  scommessaKey?: string;
  isActiveInCart: boolean;
  isQuotaVirtual?: boolean;
  containerClassName?: string;
  cartHandler: () => void;
};

export const ScommesseTipoBoxUiContainer = ({
  label,
  quota,
  isSmall,
  isDisable,
  scommessaKey,
  isQuotaVirtual,
  isActiveInCart,
  containerClassName,
  cartHandler,
}: ScommesseTipoBoxUiContainerProps) => {
  const { doCheck } = useWindowSize();
  const [isMobile] = doCheck([{ comparer: enComparer.less, size: breakpoint.lg }]);

  return (
    <button
      type="button"
      className={classNames(styles.container, containerClassName, {
        [styles.small]: isSmall,
        [styles.active]: isActiveInCart,
        [styles.disable]: isDisable,
      })}
      onClick={() => {
        scommessaKey && cartHandler(); // TODO check in ippica
      }}
    >
      {isMobile && <span className={styles.label}>{label ? label : ''}</span>}
      {/* TODO CHANGE convertDecimalQuota  */}
      <span className={styles.quota}>
        {!isDisable
          ? quota
            ? typeof quota === 'number' && !isQuotaVirtual
              ? convertDecimalQuota(quota)
              : quota
            : '>>'
          : '-'}
      </span>
    </button>
  );
};
